import { useEffect, useMemo, useState } from 'react';
import { getEditableCoaPayload } from '../utils/coaRecords';
import { mergePackageDateSources, withCanonicalDateAliases } from '../utils/printDates';
import { normalizeCoaKeys } from '../wysiwyg/coaUtils';
import { fetchProduct, type PackageCoaOption, type Product } from '../wysiwyg/packageUtils';
import { getCOAByPackageId } from './usePackages';
import { withTacPackageIdentity } from '../utils/tac';

type EditorPackageCoaState = {
  key: string;
  coa: Record<string, any>;
  product: Product | Record<string, never>;
  isLoadingCoa: boolean;
};

export type EditorPackageCoaResult = {
  coa: Record<string, any>;
  product: Product | Record<string, never>;
  isLoadingCoa: boolean;
};

const parsePackageCoa = (
  rawResponse: unknown,
  packageData: Record<string, any>,
): Record<string, any> => {
  const coaRecord: any = getEditableCoaPayload(rawResponse as any) ?? rawResponse;
  let parsedData: any = null;

  if (typeof coaRecord?.data === 'string') {
    try {
      parsedData = JSON.parse(coaRecord.data);
      while (typeof parsedData === 'string') {
        parsedData = JSON.parse(parsedData);
      }
    } catch {
      parsedData = null;
    }
  } else if (typeof coaRecord?.data === 'object' && coaRecord.data !== null) {
    parsedData = coaRecord.data;
  }

  const normalizedCoaData = withCanonicalDateAliases(normalizeCoaKeys({
    ...(coaRecord || {}),
    ...(parsedData || {}),
    coaId: coaRecord?.id,
  }));
  return mergePackageDateSources(normalizedCoaData, packageData);
};

export function useEditorPackageCoa(
  organizationId: number | null | undefined,
  packageOption: PackageCoaOption | null,
): EditorPackageCoaResult {
  const requestKey = JSON.stringify([
    String(organizationId ?? ''),
    packageOption?.id ?? null,
    packageOption?.productId ?? null,
  ]);
  const hasOrganization = organizationId !== null && organizationId !== undefined;
  const isStaged = Boolean(packageOption?.id.startsWith('staged-'));
  const loadsCoa = Boolean(packageOption && hasOrganization && !isStaged);
  const [state, setState] = useState<EditorPackageCoaState | null>(null);

  useEffect(() => {
    if (!packageOption || !hasOrganization) {
      setState({
        key: requestKey,
        coa: packageOption?.coaData ?? {},
        product: {},
        isLoadingCoa: false,
      });
      return;
    }

    let cancelled = false;
    setState({
      key: requestKey,
      coa: packageOption.coaData,
      product: {},
      isLoadingCoa: !isStaged,
    });

    // Loading COA ends when the COA settles; the resolved data still waits for the product.
    const coaRequest = isStaged
      ? Promise.resolve(packageOption.coaData)
      : getCOAByPackageId(organizationId, packageOption.id)
        .then((response) => parsePackageCoa(response, packageOption.coaData))
        .catch((error: unknown) => {
          console.error('[handleCoaChange] Failed to fetch COA:', error);
          return packageOption.coaData;
        })
        .then((coa) => {
          if (!cancelled) {
            setState((current) => (current?.key === requestKey ? { ...current, isLoadingCoa: false } : current));
          }
          return coa;
        });
    const productRequest = packageOption.productId
      ? fetchProduct(organizationId, packageOption.productId, '[handleCoaChange]')
        .then((product) => product ?? {})
        .catch(() => ({}))
      : Promise.resolve({});

    Promise.all([coaRequest, productRequest]).then(([coa, product]) => {
      if (cancelled) return;
      setState({
        key: requestKey,
        coa,
        product,
        isLoadingCoa: false,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [hasOrganization, isStaged, organizationId, packageOption, requestKey]);

  const isCurrent = state?.key === requestKey;
  const coa = useMemo(() => withTacPackageIdentity(
      isCurrent && state ? state.coa : packageOption?.coaData ?? {},
      organizationId, packageOption?.facilityId,
      isStaged ? null : packageOption?.id,
      isStaged && packageOption?.id.match(/^staged-\d+$/) ? packageOption.id.slice(7) : null,
    ), [isCurrent, state, packageOption, organizationId, isStaged]);
  return {
    coa,
    product: isCurrent && state ? state.product : {},
    isLoadingCoa: loadsCoa && (!isCurrent || Boolean(state?.isLoadingCoa)),
  };
}